import { Alert, TextField } from '@mui/material';
import { useState } from 'react';

import { recordPayment } from '../../api/admin/endpoints';
import type { InvoiceDetail, PaymentInput } from '../../api/admin/types';
import { useAppMutation } from '../../api/useAppMutation';
import { FormDialog } from '../../components/FormDialog';
import { moneyInputValue, parseMoneyInput, todayInputValue } from '../../components/parse';
import { useT } from '../../i18n/LanguageContext';

type RecordPaymentDialogProps = { invoice: InvoiceDetail; onClose: () => void };

/** A transfer matched by hand to a proforma. Prefilled with what remains (R0006 D-09). */
export const RecordPaymentDialog = ({ invoice, onClose }: RecordPaymentDialogProps) => {
  const t = useT();
  const [amount, setAmount] = useState(moneyInputValue(Math.max(0, invoice.totalMinor - invoice.paidMinor)));
  const [receivedOn, setReceivedOn] = useState(todayInputValue());
  const [reference, setReference] = useState('');
  const [note, setNote] = useState('');
  const [problem, setProblem] = useState<string | null>(null);
  const mutation = useAppMutation(
    (input: PaymentInput) => recordPayment(invoice.id, input),
    ['invoice', 'invoices', 'tenant', 'tenants', 'audit'],
    onClose,
  );

  const submit = (): void => {
    const amountMinor = parseMoneyInput(amount);

    if (typeof amountMinor !== 'number' || amountMinor <= 0) {
      setProblem(t('payment.invalidAmount'));

      return;
    }

    setProblem(null);
    mutation.mutate({
      amountMinor,
      receivedOn,
      reference: reference.trim() || undefined,
      note: note.trim() || undefined,
    });
  };

  return (
    <FormDialog
      title={t('payment.title', { number: invoice.number })}
      onClose={onClose}
      onSubmit={submit}
      submitLabel={t('invoice.recordPayment')}
      isBusy={mutation.isPending}
      error={mutation.error}
      problem={problem}
    >
      <Alert severity="info" variant="outlined">
        {t('payment.hint')}
      </Alert>
      <TextField
        required
        label={t('field.amount')}
        value={amount}
        inputProps={{ inputMode: 'decimal' }}
        onChange={(event) => setAmount(event.target.value)}
      />
      <TextField
        required
        type="date"
        label={t('field.receivedOn')}
        value={receivedOn}
        onChange={(event) => setReceivedOn(event.target.value)}
        InputLabelProps={{ shrink: true }}
      />
      <TextField
        label={t('field.paymentReference')}
        value={reference}
        onChange={(event) => setReference(event.target.value)}
      />
      <TextField
        multiline
        minRows={2}
        label={t('field.note')}
        value={note}
        onChange={(event) => setNote(event.target.value)}
      />
    </FormDialog>
  );
};
